import { useEffect } from 'react';

import { useLocationContext } from './location.provider';
import { useOnboardingContext } from './onboarding/onboarding.provider';
import { useUserContext } from './user/user.provider';
import { locationFromState } from '../location/location';

/**
 * Overrides the IP/timezone-derived `location` with the strongest available
 * address signal. LocationProvider sits above User/Onboarding and can't read
 * them itself; this component renders inside both, pushes the derived location
 * up, and renders nothing (mirrors LocaleUserSync).
 *
 * Precedence is enforced by the source rank in LocationProvider.setLocation:
 *   - The authenticated user's `currentAddress.state` ('user') wins over
 *     everything.
 *   - The onboarding address ('address') wins over IP/timezone but not over a
 *     signed-in user's persisted address.
 * A late-arriving IP result can never clobber either of these.
 */
export const LocationAddressSync = (): null => {
  const { setLocation } = useLocationContext();
  const { user } = useUserContext();
  const onboarding = useOnboardingContext();

  // Authenticated user's persisted address — the strongest signal.
  const userState = user?.currentAddress?.state;
  useEffect(() => {
    const derived = locationFromState(userState);
    if (derived) setLocation(derived, 'user');
  }, [userState, setLocation]);

  // Address entered during onboarding (pre-account) — overrides IP/timezone.
  const onboardingState = onboarding?.personalInfo?.address?.state;
  useEffect(() => {
    const derived = locationFromState(onboardingState);
    if (derived) setLocation(derived, 'address');
  }, [onboardingState, setLocation]);

  return null;
};
